import React from 'react';
import { Dropdown } from 'react-bootstrap';
import SoftBadge from 'components/common/SoftBadge';
import CardDropdown from 'components/common/CardDropdown';

/**
 * @function CandidatesColumns
 * @description Define las columnas de la tabla de candidatos. Incluye datos como nombre, correo, colegio, certificación,
 * fechas clave y opciones de acciones (editar, resetear contraseña, eliminar).
 *
 * @param {Function} handleEditUser - Función que se ejecuta al hacer clic en "Editar".
 * @param {Function} handleResetPassword - Función que se ejecuta al hacer clic en "Resetear Contraseña".
 * @param {Function} handleDeleteUser - Función que se ejecuta al hacer clic en "Eliminar".
 * 
 * @returns {Array} Arreglo de objetos de configuración de columnas para una tabla avanzada.
 *
 * @example
 * <AdvanceTable columns={CandidatesColumns(edit, reset, delete)} />
 */
const CandidatesColumns = (handleEditUser, handleResetPassword, handleDeleteUser) => [
    {
        accessor: 'index',
        Header: '#',
        headerProps: {
            style: { width: '30px', minWidth: '30px', maxWidth: '50px' }
        },
        cellProps: {
            style: { width: '30px', minWidth: '30px', maxWidth: '50px' }
        },
        Cell: rowData => {
            const { index } = rowData.row.original;
            return index+1;
        }
    },
    {
        accessor: 'none',
        Header: '',
        disableSortBy: true,
        headerProps: {
            style: { width: '50px', minWidth: '50px', maxWidth: '50px' }
        },
        cellProps: {
            className: 'text-end',
            style: { width: '50px', minWidth: '50px', maxWidth: '50px' }
        },
        /**
         * @param {Object} rowData - Datos de la fila actual
         * @returns {JSX.Element} Menú de acciones: editar, resetear contraseña, eliminar
         */
        Cell: rowData => {
            const { user_id, certification_id, name, username, email, certification_status } = rowData.row.original;
            return (
                <CardDropdown>
                    <div className="py-2">
                        <Dropdown.Item onClick={() => handleEditUser(user_id, certification_id)}>Editar</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleResetPassword(user_id, name, username, email)}>Reestablecer Contraseña</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                            className="text-danger"
                            onClick={() => handleDeleteUser(user_id, certification_id, name, certification_status)}
                        >
                            Eliminar
                        </Dropdown.Item>
                    </div>
                </CardDropdown>
            );
        }
    },
    {
        accessor: 'user_id',
        Header: 'ID',
        headerProps: { className: 'hidden-column' },
        cellProps: { className: 'hidden-column' }
    },
    {
        accessor: 'certification_id',
        Header: 'CID',
        headerProps: { className: 'hidden-column' },
        cellProps: { className: 'hidden-column' }
    },
    {
        accessor: 'name',
        Header: 'Nombre',
        headerProps: { className: 'ps-2' },
        cellProps: {
            className: 'white-space-nowrap pe-5 ps-2 py-2 fw-bold'
        }
    },
    {
        accessor: 'email',
        Header: 'Email',
        headerProps: { className: 'ps-2' },
        cellProps: { className: 'ps-2' }
    },
    {
        accessor: 'college_name',
        Header: 'Colegio',
        headerProps: {
            className: 'ps-2',
            style: { width: '150px', minWidth: '150px', maxWidth: '200px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '150px', minWidth: '150px', maxWidth: '200px' }
        }
    },
    {
        accessor: 'catalog_id',
        Header: 'Certificación',
        headerProps: {
            className: 'ps-2',
            style: { width: '120px', minWidth: '120px', maxWidth: '150px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '120px', minWidth: '120px', maxWidth: '150px' }
        }
    },
    {
        accessor: 'candidate_type',
        Header: 'Candidato',
        headerProps: {
            className: 'ps-2',
            style: { width: '120px', minWidth: '120px', maxWidth: '120px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '120px', minWidth: '120px', maxWidth: '120px' }
        }
    },
    {
        accessor: 'certification_type',
        Header: 'Tipo',
        headerProps: {
            className: 'ps-2',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        }
    },
    {
        accessor: 'certification_status',
        Header: 'Estatus',
        headerProps: {
            className: 'ps-2',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        }
    },
    {
        accessor: 'assigned_date',
        Header: 'Fecha Inicio',
        headerProps: {
            className: 'ps-2',
            style: { width: '110px', minWidth: '110px', maxWidth: '110px' }
        },
        cellProps: {
            className: 'ps-2 text-center',
            style: { width: '110px', minWidth: '110px', maxWidth: '110px' }
        }
    },
    {
        accessor: 'closed_date',
        Header: 'Fecha Fin',
        headerProps: {
            className: 'ps-2',
            style: { width: '110px', minWidth: '110px', maxWidth: '110px' }
        },
        cellProps: {
            className: 'ps-2 text-center',
            style: { width: '110px', minWidth: '110px', maxWidth: '110px' }
        }
    },
    {
        accessor: 'completed_date',
        Header: 'Fecha Finalizado',
        headerProps: {
            className: 'ps-2',
            style: { width: '110px', minWidth: '110px', maxWidth: '110px' }
        },
        cellProps: {
            className: 'ps-2 text-center',
            style: { width: '110px', minWidth: '110px', maxWidth: '110px' }
        }
    },
    {
        accessor: 'status',
        Header: '',
        headerProps: {
            className: 'ps-2 text-center white-space-nowrap',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        },
        cellProps: {
            className: 'ps-2 font-sans-serif fs-0 text-center',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        },
        /**
         * @param {Object} rowData - Datos de la fila actual
         * @returns {JSX.Element} Badge con el estatus del candidato o N/A
         */
        Cell: rowData => {
            const { status } = rowData.row.original;
            return status ? (
                <SoftBadge bg={status.type}>{status.content}</SoftBadge>
            ) : (
                <p className="mb-0 text-500 font-sans-serif fs--1">N/A</p>
            );
        }
    }
];

export default CandidatesColumns;